import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { QuizIntro } from '@/components/quiz/QuizIntro';
import { QuizQuestionCard } from '@/components/quiz/QuizQuestionCard';
import { QuizProgress } from '@/components/quiz/QuizProgress';
import { QuizScore } from '@/components/quiz/QuizScore';
import { quizzesService } from '@/services/quizzes.service';
import { coinsService } from '@/services/coins.service';
import { ApiError } from '@/services/http';
import { useReadersStore } from '@/store/readers.store';
import { cn } from '@/lib/cn';
import type { AnswerResult, AttemptCompletionResult, QuizView } from '@/types/api';

/**
 * Quiz page — `/quiz/:quizId`. A single route that internally walks
 * through three phases per the design spec:
 *
 *    intro   → (first entry only) badge teaser + "Start quiz"
 *    asking  → Q1..QN; per-question feedback; auto-advance with override
 *    reviewing → medallion, badge card, answer review, footer actions
 *
 * The attempt lifecycle:
 *   1. On first option select we POST `/quizzes/:id/attempts` to get an
 *      attempt id.
 *   2. Each selection POSTs `/attempts/:id/answers` and returns the
 *      correct option + explanation for the feedback card.
 *   3. On the last question's auto-advance we POST
 *      `/attempts/:id/complete` which finalizes the score and awards
 *      the badge if the threshold is met.
 *
 * Match the spec: commit-on-tap, wrong answers never re-prompt, every
 * answer (correct or wrong) gets an explanation, auto-advance pauses on
 * "Next" focus, keyboard shortcuts 1–4 / A–D / Enter / Esc.
 */

type Phase = 'intro' | 'asking' | 'reviewing';

interface AskingState {
  currentIndex: number;
  /** questionId → optionId */
  picks: Record<string, string>;
  /** questionId → AnswerResult from the server (answer feedback) */
  results: Record<string, AnswerResult>;
  /** True while the latest POST answer is in flight (debounces rapid taps) */
  submitting: boolean;
}

export function QuizPage() {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const selectedReader = useReadersStore((s) => s.selectedReader());
  const incrementCoinBalance = useReadersStore((s) => s.incrementCoinBalance);

  const [quiz, setQuiz] = useState<QuizView | null>(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<Phase>('intro');
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [asking, setAsking] = useState<AskingState>({
    currentIndex: 0,
    picks: {},
    results: {},
    submitting: false,
  });
  const [completion, setCompletion] = useState<AttemptCompletionResult | null>(null);
  const [alreadyEarnedCoins, setAlreadyEarnedCoins] = useState(false);

  // --- Load the quiz ----------------------------------------------------
  useEffect(() => {
    if (!quizId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const row = await quizzesService.getById(quizId);
        if (!cancelled) setQuiz(row);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not open the quiz.');
        navigate('/learns');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [quizId, navigate]);

  // --- Check if coins were already earned from this quiz ----------------
  useEffect(() => {
    if (!quizId || !selectedReader) return;
    let cancelled = false;
    coinsService
      .hasEarnedFromQuiz(selectedReader.id, quizId)
      .then((res) => {
        if (!cancelled) setAlreadyEarnedCoins(res.earned);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [quizId, selectedReader]);

  // --- Reset state helpers ----------------------------------------------
  const resetAttempt = useCallback(() => {
    setAttemptId(null);
    setAsking({
      currentIndex: 0,
      picks: {},
      results: {},
      submitting: false,
    });
    setCompletion(null);
  }, []);

  // --- Start / select / complete ----------------------------------------
  const ensureAttempt = useCallback(async (): Promise<string | null> => {
    if (attemptId) return attemptId;
    if (!quiz || !selectedReader) return null;
    try {
      const res = await quizzesService.startAttempt(quiz.id, selectedReader.id);
      setAttemptId(res.attemptId);
      return res.attemptId;
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not start the quiz.');
      return null;
    }
  }, [attemptId, quiz, selectedReader]);

  const handleSelect = useCallback(
    async (questionId: string, optionId: string) => {
      if (!quiz || !selectedReader) return;
      if (asking.picks[questionId]) return; // already answered, frozen per spec
      if (asking.submitting) return;

      setAsking((prev) => ({
        ...prev,
        picks: { ...prev.picks, [questionId]: optionId },
        submitting: true,
      }));

      const id = await ensureAttempt();
      if (!id) {
        setAsking((prev) => ({ ...prev, submitting: false }));
        return;
      }

      try {
        const result = await quizzesService.recordAnswer(id, {
          readerId: selectedReader.id,
          questionId,
          optionId,
        });
        setAsking((prev) => ({
          ...prev,
          results: { ...prev.results, [questionId]: result },
          submitting: false,
        }));
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Could not record your answer.');
        // Roll back the pick so the reader can try again.
        setAsking((prev) => {
          const { [questionId]: _discard, ...restPicks } = prev.picks;
          return { ...prev, picks: restPicks, submitting: false };
        });
      }
    },
    [quiz, selectedReader, asking.picks, asking.submitting, ensureAttempt],
  );

  const advanceTo = useCallback(
    (nextIndex: number) => {
      if (!quiz) return;
      if (nextIndex < quiz.questions.length) {
        setAsking((prev) => ({ ...prev, currentIndex: nextIndex }));
      } else {
        // End of quiz — complete the attempt.
        (async () => {
          if (!attemptId || !selectedReader) return;
          try {
            const res = await quizzesService.completeAttempt(attemptId, selectedReader.id);
            setCompletion(res);
            setPhase('reviewing');
            // Optimistic coin-balance bump so the header chip reflects
            // the reward immediately — the canonical value will be
            // refetched next time the summary endpoint is polled.
            // No-ops when the balance hasn't been hydrated yet (store
            // will pull the canonical number on next mount).
            if (res.coinsEarned > 0) {
              incrementCoinBalance(selectedReader.id, res.coinsEarned);
            }
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not finish the quiz.');
          }
        })();
      }
    },
    [quiz, attemptId, selectedReader],
  );

  // --- Retry --------------------------------------------------------------
  const handleTryAgain = useCallback(() => {
    resetAttempt();
    setPhase('asking');
  }, [resetAttempt]);

  // --- Parent routing helpers --------------------------------------------
  const handleBackToParent = useCallback(() => {
    if (!quiz) {
      navigate('/learns');
      return;
    }
    if (quiz.articleId) {
      // We have the article id but the reader route is keyed on slug.
      // Fallback: learns landing. Callers that want "back to article"
      // pass the article slug in history state.
      const state = (window.history.state as { usr?: { articleSlug?: string } } | null)?.usr;
      if (state?.articleSlug) {
        navigate(`/learns/articles/${state.articleSlug}`);
      } else {
        navigate('/learns');
      }
    } else if (quiz.bookId) {
      // Books are opened in a modal, not a route. Return to the
      // library where the CTA was.
      navigate('/library');
    } else {
      navigate('/learns');
    }
  }, [quiz, navigate]);

  // Primary "done" action on the score screen. A book quiz returns the reader
  // to the Library (books open as modals there); an article quiz goes to the
  // Learns landing to browse more articles.
  const handleFinish = useCallback(
    () => navigate(quiz?.bookId ? '/library' : '/learns'),
    [quiz, navigate],
  );

  // --- Keyboard shortcuts -------------------------------------------------
  useEffect(() => {
    if (phase !== 'asking' || !quiz) return;
    const onKey = (event: KeyboardEvent) => {
      const currentQuestion = quiz.questions[asking.currentIndex];
      if (!currentQuestion) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        const ok = window.confirm('Quit the quiz? Your answers so far are saved.');
        if (ok) handleBackToParent();
        return;
      }

      // Selection shortcuts
      const picked = asking.picks[currentQuestion.id];
      if (!picked) {
        const lower = event.key.toLowerCase();
        const numberIndex = '12345'.indexOf(event.key) >= 0 ? Number(event.key) - 1 : -1;
        const letterIndex = 'abcdefgh'.indexOf(lower);
        const index = numberIndex >= 0 ? numberIndex : letterIndex;
        if (index >= 0 && index < currentQuestion.options.length) {
          event.preventDefault();
          const opt = currentQuestion.options[index];
          if (opt) handleSelect(currentQuestion.id, opt.id);
          return;
        }
      }

      // Advance shortcuts (after an answer)
      if ((event.key === 'Enter' || event.key === 'ArrowRight') && picked) {
        event.preventDefault();
        advanceTo(asking.currentIndex + 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, quiz, asking, handleSelect, advanceTo, handleBackToParent]);

  // --- Derived ------------------------------------------------------------
  const summaryPerQuestion = useMemo(() => {
    if (!quiz) return [];
    return quiz.questions.map((q) => {
      const picked = asking.picks[q.id] ?? null;
      const result = asking.results[q.id] ?? null;
      return {
        questionId: q.id,
        picked,
        isCorrect: result?.isCorrect ?? null,
      };
    });
  }, [quiz, asking.picks, asking.results]);

  // --- Render ---------------------------------------------------------
  if (loading) {
    return (
      <AppShell mode="kid" className="mx-auto w-full max-w-[780px] px-6 py-16">
        <p className="text-center text-or-slate-500">Loading quiz…</p>
      </AppShell>
    );
  }

  if (!quiz) {
    return (
      <AppShell mode="kid" className="mx-auto w-full max-w-[780px] px-6 py-16">
        <div className="text-center">
          <h1 className="font-display text-[32px] font-bold text-or-slate-900">Quiz not found</h1>
          <button
            type="button"
            onClick={() => navigate('/learns')}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-or-slate-200 bg-white px-4 py-2 text-[13px] font-semibold text-or-slate-700 hover:bg-or-slate-50"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to Ollie Learns
          </button>
        </div>
      </AppShell>
    );
  }

  // No reader selected — bounce with a friendly message.
  if (!selectedReader) {
    return (
      <AppShell mode="kid" className="mx-auto w-full max-w-[780px] px-6 py-16">
        <div className="text-center">
          <h1 className="font-display text-[28px] font-bold text-or-slate-900">
            Pick a reader first
          </h1>
          <p className="mt-2 text-or-slate-500">
            Quizzes are saved to each reader's profile, so we need to know whose quiz this is.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell mode="kid" className="mx-auto w-full max-w-[780px] px-6 pb-24 pt-6">
      {/* Top bar: back to article + progress */}
      {phase === 'asking' ? (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleBackToParent}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-or-slate-500 hover:text-or-slate-700"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Back
          </button>
          <QuizProgress
            total={quiz.questions.length}
            current={asking.currentIndex}
            perQuestion={summaryPerQuestion}
          />
        </div>
      ) : null}

      {/* Phase body */}
      <div className="mt-6">
        {phase === 'intro' ? (
          <QuizIntro
            quiz={quiz}
            onStart={() => setPhase('asking')}
            onBack={handleBackToParent}
            alreadyEarnedCoins={alreadyEarnedCoins}
          />
        ) : null}

        {phase === 'asking' ? (
          <QuizQuestionCard
            key={quiz.questions[asking.currentIndex]?.id ?? asking.currentIndex}
            questionIndex={asking.currentIndex}
            totalQuestions={quiz.questions.length}
            question={quiz.questions[asking.currentIndex]!}
            picked={asking.picks[quiz.questions[asking.currentIndex]!.id] ?? null}
            result={asking.results[quiz.questions[asking.currentIndex]!.id] ?? null}
            onSelect={handleSelect}
            onAdvance={() => advanceTo(asking.currentIndex + 1)}
          />
        ) : null}

        {phase === 'reviewing' && completion ? (
          <QuizScore
            quiz={quiz}
            completion={completion}
            picks={asking.picks}
            results={asking.results}
            onTryAgain={handleTryAgain}
            onBackToParent={handleBackToParent}
            onMore={handleFinish}
          />
        ) : null}
      </div>

      {/* Hide unused imports */}
      <span className="sr-only">
        <ArrowRight className="h-0 w-0" />
        <Check className="h-0 w-0" />
        <X className="h-0 w-0" />
        <Sparkles className="h-0 w-0" />
      </span>
      <span className={cn('hidden')} />
    </AppShell>
  );
}
